import React from 'react';
import { Link } from 'react-router-dom';

const OurStory = () => {
  return (
    <div className="bg-white">
      {/* Page Header */}
      <div className="bg-apn-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* APN Logo */}
            <div className="flex justify-center mb-4">
              <img
                src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-white.jpg`}
                alt="APN Logo"
                className="h-16"
              />
            </div>
            <h1 className="text-3xl font-bold">Our Story</h1>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-lg mx-auto max-w-5xl">
          <p className="text-xl mb-8 leading-relaxed">
            Accelerated Professionals Network is a community of finance professionals collaborating to create a more seamless M&A transaction process. Catering to junior professionals (non-partners), APN encourages those in the financial industry to begin building their networks early in their careers resulting in more transparency into the full life cycle of a deal, an increase in the potential for expedited career growth and development, and greater access to resources as they continue to advance within their roles/organizations.
          </p>

          <p className="text-xl mb-8 leading-relaxed">
            APN Founder, Caroline Burnham, noticed most financial industry conferences and events were predominantly attended by Partners, Principals, and other seasoned, senior level personnel. Observing this, she found there was a significant divide within the industry and immediately recognized why: with deals on the line, it is difficult for firms to justify the risk, expense, and exposure of sending those other than their most accomplished, senior level resources, to these events. This represents an endless catch 22 cycle for emerging financial industry professionals, hoping to break the barrier to entry, and elevate their careers through networking with other well-connected professionals. Unfortunately, promising junior level talent are often precluded from participating in the very network which could increase such career development opportunities. APN was formed to address this need, to fill the networking gap between knowledge and wisdom, education and technology, energy and instinct, mentor and rising talent, and to provide a much-needed platform for motivated and likeminded contemporaries within the deal cycle, to connect with one another.
          </p>

          <h2 className="text-3xl font-bold mt-12 mb-6">Who We Welcome</h2>
          <p className="text-xl mb-6 leading-relaxed">
            APN welcomes the following professionals from the fields of finance and deal making:
          </p>
          <ul className="list-disc pl-8 mb-12 text-xl space-y-2">
            <li>Investment Banking</li>
            <li>Private Equity</li>
            <li>Private Family Offices</li>
            <li>Real Estate & Development</li>
            <li>Accounting</li>
            <li>Tax Services</li>
            <li>Insurances</li>
            <li>Wealth Management/Financial Planning and Advising</li>
            <li>Law</li>
            <li>Venture Capital</li>
            <li>Business Advising & Strategy</li>
            <li>Tech</li>
            <li>Independent Consulting</li>
            <li>Debt/Credit Providers</li>
            <li>Management Consulting</li>
            <li>Capital Markets</li>
            <li>Any other services that may be involved in a transaction</li>
          </ul>

          {/* Founder Section */}
          <div className="mt-16 mb-12">
            <h2 className="text-3xl font-bold mb-8">About Our Founder</h2>
            <div className="md:flex items-start">
              <div className="md:w-1/3 mb-6 md:mb-0 md:pr-8">
                <img
                  src="/apn-logos/caroline-header-photo.jpeg"
                  alt="Caroline Burnham"
                  className="rounded-lg shadow-md w-full"
                />
              </div>
              <div className="md:w-2/3">
                <p className="text-xl mb-6 leading-relaxed">
                  Caroline Burnham is the founder of APN and currently serves as the Director and Head of Business Development and Marketing at Ellenoff Grossman and Schole LLP [EGS], a New York City-based law firm specializing in Corporate/Securities, Taxation and Real Estate, Litigation, Intellectual Property, Labor and Employment, Bankruptcy, and Immigration law. She maintains active engagement with industry leaders at conferences, events, and panels, cultivating strategic relationships and advancing opportunities across the mergers and acquisitions and transaction advisory landscape.
                </p>
                <p className="text-xl mb-6 leading-relaxed">
                  Leveraging a wealth of expertise, Caroline collaborates with trusted advisors, investors, and corporations, both public and private, to provide legal advisory services related to mergers, acquisitions, divestitures, restructurings, financings, corporate tax matters, estate and gift tax, insurance placement, and litigation support. She leads the firm's client acquisition and cultivation efforts by designing and executing targeted go-to market strategies. Her responsibilities include developing conference and marketing initiatives and overseeing the evaluation and deployment of business development technologies, including customer relationship management systems, email campaign platforms, and artificial intelligence applications.
                </p>
                <p className="text-xl mb-6 leading-relaxed">
                  Prior to joining EGS, Caroline developed her networking and business development skills at Marshall and Stevens, a full-service valuation firm serving the financial services sector. Preceding in her role at Marshall and Stevens, Caroline began her business development career at SourceScrub, where she was responsible for engaging with industry leaders across the Northeast and European markets.
                </p>
                <p className="text-xl mb-6 leading-relaxed">
                  Caroline looks back fondly at her time at Penn State as well as studying the University of Westminster (London, UK), and working at the Royal Albert Hall in South Kensington, London.
                </p>
                <p className="text-xl mb-6 leading-relaxed">
                  In addition to her professional achievements, she has a strong commitment to philanthropy. She has served as an Advisory Board Member, Marketing Committee Member, and Chairman of the Financial Reform Committee for TEACH, a nonprofit organization dedicated to the education and support of hospitalized children.
                </p>
                <p className="text-xl mb-6 leading-relaxed">
                  APN was established in response to a critical industry imbalance observed by its founder. Conferences and professional events are frequently dominated by partners, principals, and senior executives, leaving limited opportunity for emerging talent. APN bridges this divide by fostering connections between knowledge and experience, education and innovation, and mentors and rising professionals. The platform enables motivated individuals across the M&A transaction lifecycle to connect, collaborate, and progress through curated local events and nationwide virtual networking forums.
                </p>
              </div>
            </div>
          </div>

          {/* Social Media Links */}
          <div className="mt-16 text-center">
            <h3 className="text-2xl font-bold mb-6">Connect With Us</h3>
            <div className="flex justify-center space-x-8">
              <a
                href="https://www.instagram.com/the_apn_/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-apn-blue hover:text-apn-light-blue transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
              <a
                href="https://www.linkedin.com/company/accelerated-professionals-network/?viewAsMember=true"
                target="_blank"
                rel="noopener noreferrer"
                className="text-apn-blue hover:text-apn-light-blue transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <section className="bg-apn-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* APN Logo */}
          <div className="flex justify-center mb-6">
            <img
              src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-SQ-white.jpg`}
              alt="APN Logo Square"
              className="h-20"
            />
          </div>
          <h2 className="text-3xl font-bold mb-4">Ready to Join the Network?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Connect with finance professionals, access exclusive resources, and accelerate your career with APN.
          </p>
          <Link to="/about/membership" className="btn-primary bg-white text-apn-blue hover:bg-gray-100">
            Become a Member Today
          </Link>
        </div>
      </section>
    </div>
  );
};

export default OurStory;
